// components/Inventario.jsx
import React, { useState, useEffect } from 'react';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';
import { useTranslation } from '../hooks/useTranslation';
import { formatMoneyUniversal } from '../util/formatMoneyUniversal';

const Inventario = ({ usuarioActual }) => {
  const { t, idioma } = useTranslation();
  const [productos, setProductos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [pagina, setPagina] = useState(1);
  const [mostrarAgotados, setMostrarAgotados] = useState(false);
  const productosPorPagina = 10;
  const db = getFirestore();

  // Obtener país del usuario para formateo de moneda
  const paisCodigo = usuarioActual?.pais || 'CO';

  useEffect(() => {
    const cargarInventario = async () => {
      if (!usuarioActual?.uid) {
        setCargando(false);
        return;
      }
      try {
        const q = query(
          collection(db, 'inventario'),
          where('userId', '==', usuarioActual.uid)
        );
        const snapshot = await getDocs(q);
        let productosInventario = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Filtrar productos con stock 0 si no se muestra agotados
        if (!mostrarAgotados) {
          productosInventario = productosInventario.filter(p => (p.cantidad || 0) > 0);
        }
        
        // Ordenar por nombre
        productosInventario.sort((a, b) => (a.producto || '').localeCompare(b.producto || ''));
        
        setProductos(productosInventario);
      } catch (error) {
        console.error('Error cargando inventario:', error);
      } finally {
        setCargando(false);
      }
    };
    cargarInventario();
  }, [usuarioActual?.uid, db, mostrarAgotados]);

  // Paginación
  const totalPaginas = Math.ceil(productos.length / productosPorPagina);
  const inicio = (pagina - 1) * productosPorPagina;
  const productosPagina = productos.slice(inicio, inicio + productosPorPagina);

  if (cargando) {
    return (
      <div className="bg-[#1e293b] rounded-2xl p-6 border border-blue-900/30">
        <div className="text-center text-gray-400 py-8">
          {idioma === 'en' ? 'Loading inventory...' : 'Cargando inventario...'}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#1e293b] rounded-2xl p-6 border border-blue-900/30">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
          📦 {idioma === 'en' ? 'Current Inventory' : 'Inventario Actual'}
        </h2>
        
        {/* Switch para mostrar/ocultar agotados */}
        <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
          <input
            type="checkbox"
            checked={mostrarAgotados}
            onChange={(e) => setMostrarAgotados(e.target.checked)}
            className="w-4 h-4 rounded border-gray-600 bg-slate-700 text-cyan-500 focus:ring-cyan-500"
          />
          {idioma === 'en' ? 'Show out of stock' : 'Mostrar productos agotados'}
        </label>
      </div>
      
      {productos.length === 0 ? (
        <p className="text-gray-400 text-center py-8">
          {idioma === 'en' 
            ? 'No products in inventory. Register a Production Order to create stock.' 
            : 'No hay productos en inventario. Registra una Orden de Producción para crear stock.'}
        </p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-gray-400 border-b border-slate-700">
                <tr>
                  <th className="text-left py-3">{idioma === 'en' ? 'Product' : 'Producto'}</th>
                  <th className="text-right py-3">{idioma === 'en' ? 'Qty' : 'Cant.'}</th>
                  {/* ✅ NUEVAS COLUMNAS: Costo Producción y Comisión */}
                  <th className="text-right py-3">{idioma === 'en' ? 'Prod. Cost' : 'Costo Prod.'}</th>
                  <th className="text-right py-3">{idioma === 'en' ? 'Commission' : 'Comisión'}</th>
                  <th className="text-right py-3">{idioma === 'en' ? 'Total Cost' : 'Costo Total'}</th>
                  <th className="text-right py-3">{idioma === 'en' ? 'Margin Ref.' : 'Margen Ref.'}</th>
                  <th className="text-right py-3">{idioma === 'en' ? 'Channel' : 'Canal'}</th>
                </tr>
              </thead>
              <tbody>
                {productosPagina.map(prod => {
                  // ✅ Calcular costos con fallback para compatibilidad
                  const costoProduccion = prod.costoProduccion || prod.costoUnitario || 0;
                  const comision = prod.comisionPlataforma || 0;
                  const costoTotal = prod.costoUnitario || (costoProduccion + comision);
                  
                  return (
                    <tr key={prod.id} className="border-b border-slate-800 hover:bg-slate-800/30 transition">
                      <td className="py-3 text-white font-medium">{prod.producto}</td>
                      <td className="py-3 text-right text-white">{prod.cantidad || 0} und</td>
                      
                      {/* ✅ Costo de Producción (sin comisión) */}
                      <td className="py-3 text-right text-emerald-400 font-medium">
                        {formatMoneyUniversal(costoProduccion, paisCodigo)}
                      </td>
                      
                      {/* ✅ Comisión del canal */}
                      <td className="py-3 text-right text-orange-400">
                        {formatMoneyUniversal(comision, paisCodigo)}
                      </td>
                      
                      {/* ✅ Costo Total (con comisión) */}
                      <td className="py-3 text-right text-cyan-400">
                        {formatMoneyUniversal(costoTotal, paisCodigo)}
                      </td>
                      
                      <td className="py-3 text-right">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                          (prod.margenReferencia || 0) >= 40 
                            ? 'bg-green-900/40 text-green-400' 
                            : (prod.margenReferencia || 0) >= 20 
                            ? 'bg-yellow-900/40 text-yellow-400' 
                            : 'bg-red-900/40 text-red-400'
                        }`}>
                          {prod.margenReferencia || 0}%
                        </span>
                      </td>
                      
                      {/* ✅ Canal de venta */}
                      <td className="py-3 text-right text-gray-400 text-xs">
                        {prod.plataformaVenta || prod.metadata?.plataforma || '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {/* Paginación */}
          {totalPaginas > 1 && (
            <div className="flex justify-center items-center gap-4 mt-6 pt-4 border-t border-slate-700">
              <button
                onClick={() => setPagina(p => Math.max(1, p - 1))}
                disabled={pagina === 1}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  pagina === 1
                    ? 'bg-slate-800 text-gray-500 cursor-not-allowed'
                    : 'bg-slate-700 text-white hover:bg-slate-600'
                }`}
              >
                ← {idioma === 'en' ? 'Previous' : 'Anterior'}
              </button>
              <span className="text-gray-400 text-sm">
                {idioma === 'en' ? 'Page' : 'Página'} {pagina} {idioma === 'en' ? 'of' : 'de'} {totalPaginas}
              </span>
              <button
                onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}
                disabled={pagina === totalPaginas}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  pagina === totalPaginas
                    ? 'bg-slate-800 text-gray-500 cursor-not-allowed'
                    : 'bg-slate-700 text-white hover:bg-slate-600'
                }`}
              >
                {idioma === 'en' ? 'Next' : 'Siguiente'} →
              </button>
            </div>
          )}
        </>
      )}
      
      {/* Footer informativo */}
      <div className="mt-6 pt-4 border-t border-slate-700 text-xs text-gray-500">
        <p>{idioma === 'en' 
          ? '📌 Products are automatically updated when registering a Production Order' 
          : '📌 Los productos se actualizan automáticamente al registrar una Orden de Producción'}</p>
        <p className="mt-1">{idioma === 'en'
          ? '💰 Weighted average cost: total inventory value divided by total quantity'
          : '💰 Costo promedio ponderado: se calcula sumando el valor total del inventario dividido por la cantidad total'}</p>
        <p className="mt-1 text-gray-600">
          {idioma === 'en'
            ? '📊 Production Cost excludes channel commission. Total Cost includes commission.'
            : '📊 Costo de Producción excluye comisión del canal. Costo Total incluye comisión.'}
        </p>
      </div>
    </div>
  );
};

export default Inventario;

